import type { Recording } from "../api/parse.ts";
import { formatDuration, formatFileSize, formatSessionDateLabelFromKey, formatSessionWhen, sessionLocalDateKey } from "./sessionWhen.ts";

export const RECORDING_FILTER_COLUMNS = ["device", "user", "start", "end", "duration", "size"] as const;

export type RecordingFilterColumn = (typeof RECORDING_FILTER_COLUMNS)[number];

export type RecordingFilters = {
  readonly values: ReadonlyMap<RecordingFilterColumn, ReadonlySet<string>>;
};

export const NO_RECORDING_FILTERS: RecordingFilters = { values: new Map() };

export type RecordingSortDirection = "asc" | "desc";

export type RecordingSort = { readonly column: RecordingFilterColumn; readonly direction: RecordingSortDirection };

export const DEFAULT_RECORDING_SORT: RecordingSort = { column: "start", direction: "desc" };

export const RECORDING_IN_PROGRESS_FILTER = "__in_progress__";

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function recordingFilterDisplayLabel(column: RecordingFilterColumn, value: string): string {
  if (column === "start" || column === "end") {
    if (value === RECORDING_IN_PROGRESS_FILTER) return "In progress";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatSessionDateLabelFromKey(value);
  }
  return value;
}

export function recordingFacetValue(recording: Recording, column: RecordingFilterColumn, timeZone?: string): string {
  switch (column) {
    case "device":
      return recording.deviceName;
    case "user":
      return recording.connectedUser;
    case "start":
      return sessionLocalDateKey(recording.startedAt, timeZone);
    case "end":
      return recording.endedAt ? sessionLocalDateKey(recording.endedAt, timeZone) : RECORDING_IN_PROGRESS_FILTER;
    case "duration":
      return formatDuration(recording.startedAt, recording.endedAt);
    case "size":
      return formatFileSize(recording.bytes);
  }
}

type RowFacets = ReadonlyMap<RecordingFilterColumn, string>;

function rowFacets(recording: Recording, timeZone?: string): RowFacets {
  return new Map(RECORDING_FILTER_COLUMNS.map((column) => [column, recordingFacetValue(recording, column, timeZone)]));
}

function passes(facets: RowFacets, column: RecordingFilterColumn, filters: RecordingFilters): boolean {
  const selected = filters.values.get(column);
  if (!selected || selected.size === 0) return true;
  return selected.has(facets.get(column) ?? "");
}

export function filterRecordingRows(rows: readonly Recording[], filters: RecordingFilters, timeZone?: string): Recording[] {
  return rows.filter((row) => {
    const facets = rowFacets(row, timeZone);
    return RECORDING_FILTER_COLUMNS.every((column) => passes(facets, column, filters));
  });
}

export type RecordingFilterOption = { readonly value: string; readonly label: string; readonly count: number };

export type RecordingColumnFacet = {
  readonly options: readonly RecordingFilterOption[];
};

export type RecordingFacets = {
  readonly rows: readonly Recording[];
  readonly columns: ReadonlyMap<RecordingFilterColumn, RecordingColumnFacet>;
};

export function computeRecordingFacets(rows: readonly Recording[], filters: RecordingFilters, timeZone?: string): RecordingFacets {
  const counts = new Map<RecordingFilterColumn, Map<string, number>>(RECORDING_FILTER_COLUMNS.map((column) => [column, new Map()]));
  const passing: Recording[] = [];
  for (const row of rows) {
    const facets = rowFacets(row, timeZone);
    const failed = RECORDING_FILTER_COLUMNS.filter((column) => !passes(facets, column, filters));
    if (failed.length === 0) passing.push(row);
    for (const column of RECORDING_FILTER_COLUMNS) {
      const counted = failed.length === 0 || (failed.length === 1 && failed[0] === column);
      const tally = counts.get(column);
      if (!tally) continue;
      const value = facets.get(column) ?? "";
      tally.set(value, (tally.get(value) ?? 0) + (counted ? 1 : 0));
    }
  }
  const columns = new Map<RecordingFilterColumn, RecordingColumnFacet>();
  for (const column of RECORDING_FILTER_COLUMNS) {
    const tally = counts.get(column) ?? new Map<string, number>();
    columns.set(column, {
      options: [...tally.keys()].sort((left, right) => collator.compare(left, right)).map((value) => ({
        value,
        label: recordingFilterDisplayLabel(column, value),
        count: tally.get(value) ?? 0,
      })),
    });
  }
  return { rows: passing, columns };
}

function filterSignature(filters: RecordingFilters): string {
  const parts: string[] = [];
  for (const column of RECORDING_FILTER_COLUMNS) {
    const selected = filters.values.get(column);
    if (!selected || selected.size === 0) continue;
    parts.push(column, ...[...selected].sort());
  }
  return parts.join("\u0000");
}

export function createRecordingFacetMemo(compute: typeof computeRecordingFacets = computeRecordingFacets) {
  let last: { rows: readonly Recording[]; signature: string; timeZone?: string; facets: RecordingFacets } | null = null;
  return (rows: readonly Recording[], filters: RecordingFilters, timeZone?: string): RecordingFacets => {
    const signature = filterSignature(filters);
    if (last && last.rows === rows && last.signature === signature && last.timeZone === timeZone) return last.facets;
    const facets = compute(rows, filters, timeZone);
    last = { rows, signature, timeZone, facets };
    return facets;
  };
}

export function toggleRecordingFilter(filters: RecordingFilters, column: RecordingFilterColumn, value: string): RecordingFilters {
  const values = new Map(filters.values);
  const selected = new Set(values.get(column) ?? []);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  if (selected.size === 0) values.delete(column);
  else values.set(column, selected);
  return { values };
}

export function selectedRecordingFilterCount(filters: RecordingFilters, column: RecordingFilterColumn): number {
  return filters.values.get(column)?.size ?? 0;
}

export function hasRecordingFilters(filters: RecordingFilters): boolean {
  return [...filters.values.values()].some((selected) => selected.size > 0);
}

function sortKey(recording: Recording, column: RecordingFilterColumn): string | number {
  switch (column) {
    case "device":
      return recording.deviceName;
    case "user":
      return recording.connectedUser;
    case "start":
      return recording.startedAt;
    case "end":
      return recording.endedAt ?? "";
    case "duration": {
      const start = Date.parse(recording.startedAt);
      const end = recording.endedAt ? Date.parse(recording.endedAt) : start;
      return Number.isFinite(start) && Number.isFinite(end) ? end - start : 0;
    }
    case "size":
      return recording.bytes;
  }
}

export function sortRecordingRows(rows: readonly Recording[], sort: RecordingSort): Recording[] {
  const copy = [...rows];
  const direction = sort.direction === "asc" ? 1 : -1;
  copy.sort((left, right) => {
    const leftKey = sortKey(left, sort.column);
    const rightKey = sortKey(right, sort.column);
    if (typeof leftKey === "number" && typeof rightKey === "number") return (leftKey - rightKey) * direction;
    return collator.compare(String(leftKey), String(rightKey)) * direction;
  });
  return copy;
}

export const RECORDING_COLUMN_LABELS: Readonly<Record<RecordingFilterColumn, string>> = {
  device: "Device",
  user: "User",
  start: "Start",
  end: "End",
  duration: "Duration",
  size: "Size",
};

/** Cell text for Start/End columns (full local date and time). */
export function recordingWhenCell(iso: string, timeZone?: string): string {
  return formatSessionWhen(iso, undefined, timeZone);
}
